import React, { useEffect, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import i18n from '../../i18n/i18n';
import NotificationToast from '../notifications/NotificationToast';
import { useTheme } from '../../contexts/ThemeContext';
import { getUserInvoicesByTaxId } from '../../api/api';
import { FormContainer } from './FormStyles';

const InfoText = styled.div`
  text-align: center;
  font-size: 1rem;
  color: #444;
  margin-bottom: 1rem;
`;

const TaxIdValue = styled.div`
  margin-top: 0.2rem;
  font-size: 1.15rem;
  font-weight: 600;
  color: #222;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const DocumentCard = styled.div`
  background: white;
  padding: 0.9rem 1.1rem;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DocTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  color: #222;
  line-height: 1.3;
`;

const DownloadButton = styled(Button)`
  && {
    min-width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.buttonColor};
    color: ${(props) => props.theme.buttonTextColor};
    box-shadow: none;
    padding: 0;
  }
`;

export default function TaxIdSearchForm({
  setLoading,
  userDetails,
  loadingDocs,
  setLoadingDocs,
}) {
  const { theme } = useTheme();
  const [notification, setNotification] = useState<any>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);

  const taxId = userDetails?.taxId || '';

  useEffect(() => {
    const fetchDocs = async () => {
      if (!taxId) return;

      setLoading(true);
      setLoadingDocs(true);

      try {
        const response = await getUserInvoicesByTaxId();
        setDocuments(response.data || []);
      } catch {
        setNotification({
          severity: 'error',
          title: i18n.t('error'),
          message: i18n.t('error_occured_try_later'),
        });
        setShowNotification(true);
      }

      setLoading(false);
      setLoadingDocs(false);
    };

    fetchDocs();
  }, [taxId]);

  const handleDownload = async (documentInfo) => {
    let url;

    if (documentInfo?.url) {
      url = documentInfo.url;
    } else if (documentInfo?.text?.includes(' ')) {
      try {
        const byteChars = atob(documentInfo.text);
        // @ts-ignore
        const byteArray = new Uint8Array([...byteChars].map((c) => c.charCodeAt(0)));
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        url = URL.createObjectURL(blob);
      } catch {
        return;
      }
    }

    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', i18n.t('document'));
      link.click();
      link.remove();
    }
  };

  return (
    <FormContainer>
      <NotificationToast
        open={showNotification}
        message={notification?.message}
        title={notification?.title}
        severity={notification?.severity}
        onClose={() => setShowNotification(false)}
      />

      <InfoText>
        {i18n.t('using_tax_id')}
        <TaxIdValue>{taxId}</TaxIdValue>
      </InfoText>

      {loadingDocs ? (
        <LoaderWrapper>
          <CircularProgress style={{ color: theme.buttonColor }} />
        </LoaderWrapper>
      ) : (
        <>
          {documents.length === 0 ? (
            <InfoText>{i18n.t('no_documents_found')}</InfoText>
          ) : (
            <DocumentsList>
              {documents.map((doc) => (
                <DocumentCard key={doc.id || doc.url || doc.text}>
                  <DocTitle>
                    {doc.type === 'CUSTOMER'
                      ? i18n.t('payment_document')
                      : i18n.t('invoice')}
                  </DocTitle>

                  <DownloadButton
                    theme={theme}
                    onClick={() => handleDownload(doc)}
                  >
                    <DownloadIcon />
                  </DownloadButton>
                </DocumentCard>
              ))}
            </DocumentsList>
          )}
        </>
      )}
    </FormContainer>
  );
}
